import type { CSSProperties, ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from './util';

export type StampTone = 'red' | 'green' | 'yellow' | 'blue' | 'ink' | 'white';

const TONE: Record<StampTone, string> = {
  red: 'bg-team-b text-white outline-white/85 [&>.stamp-label]:ink-text',
  green: 'bg-jury text-white outline-white/85 [&>.stamp-label]:ink-text',
  yellow: 'bg-yellow text-ink outline-ink/70',
  blue: 'bg-team-a text-white outline-white/85 [&>.stamp-label]:ink-text',
  ink: 'bg-ink text-yellow outline-yellow/80',
  white: 'bg-white text-ink outline-ink/60',
};

export interface StampProps {
  children: ReactNode;
  tone?: StampTone;
  /** Rotation in degrees (default −8). */
  rotate?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Slam in from above with an overshoot (re-mount with a new `key` to replay). */
  slam?: boolean;
  /**
   * Called once the entrance settles — hook the gavel sound / screen shake here. Fires right after
   * mount when `slam` is off or reduced motion is on, so side effects still happen in sync.
   */
  onLanded?: () => void;
  className?: string;
  style?: CSSProperties;
}

const SIZE = {
  sm: 'text-[18px] px-2.5 py-0.5 border-3 shadow-ink-xs',
  md: 'text-[28px] px-4 py-1 border-4 shadow-ink-sm',
  lg: 'text-[44px] px-6 py-1.5 border-4 shadow-ink',
  xl: 'text-[72px] px-8 py-2 border-[5px] shadow-ink-lg',
} as const;

/**
 * Rubber-stamp lettering ("SATILDI!", "KELEPİR", "KAZIKLANDI"). A double rule (ink border + inner
 * white hairline) gives it the inked-stamp feel.
 */
export function Stamp({
  children,
  tone = 'red',
  rotate = -8,
  size = 'md',
  slam,
  onLanded,
  className,
  style,
}: StampProps) {
  const reduce = useReducedMotion();
  const animated = slam && !reduce;
  return (
    <motion.div
      initial={animated ? { scale: 3.2, opacity: 0, rotate: rotate - 14 } : false}
      animate={{ scale: 1, opacity: 1, rotate }}
      transition={
        animated ? { type: 'spring', stiffness: 640, damping: 20, mass: 0.9 } : { duration: 0 }
      }
      onAnimationComplete={() => onLanded?.()}
      className={cn(
        'comic relative inline-flex items-center justify-center border-ink whitespace-nowrap',
        'outline-2 -outline-offset-[7px]',
        TONE[tone],
        SIZE[size],
        className,
      )}
      style={style}
    >
      <span className="stamp-label pt-[0.06em]">{children}</span>
    </motion.div>
  );
}

export type BadgeTone = StampTone | 'outline';

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  /** Small tilt in degrees. */
  rotate?: number;
  /** Wobble once on mount (verdict badges). */
  wobble?: boolean;
  icon?: ReactNode;
  /** `sm` is the tiny corner tag (shelf verdicts); `md` (default) the regular chip. */
  size?: 'sm' | 'md';
  className?: string;
  title?: string;
}

const BADGE_SIZE = {
  sm: 'px-1 py-[2px] text-[11px]',
  md: 'px-1.5 py-[3px] text-[14px]',
} as const;

const BADGE_TONE: Record<BadgeTone, string> = {
  red: 'bg-team-b text-white',
  green: 'bg-jury text-white',
  yellow: 'bg-yellow text-ink',
  blue: 'bg-team-a text-white',
  ink: 'bg-ink text-yellow',
  white: 'bg-white text-ink',
  outline: 'bg-transparent text-ink',
};

/** Compact comic label chip (verdicts, tiers, counters). */
export function Badge({
  children,
  tone = 'yellow',
  rotate = 0,
  wobble,
  icon,
  size = 'md',
  className,
  title,
}: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'comic inline-flex items-center gap-1 border-2 border-ink leading-none whitespace-nowrap shadow-ink-xs',
        BADGE_SIZE[size],
        BADGE_TONE[tone],
        wobble && 'animate-wobble',
        className,
      )}
      style={{ rotate: `${rotate}deg`, ['--wobble-base' as string]: '0deg' }}
    >
      {icon}
      <span className="pt-[0.08em]">{children}</span>
    </span>
  );
}
